import { useState } from 'react';
import { RecordConversations } from '../components/messaging/RecordConversations';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Mail,
  Check,
  Archive,
  RefreshCw,
  ArrowRightCircle,
  ShieldAlert,
  ShieldOff,
} from 'lucide-react';
import type { Inquiry, InquiryStatus } from '@peles/shared';
import { getInquiries, updateInquiryStatus, convertInquiryToEvent } from '../api/inquiries';
import { InquiryActionButton } from '../components/inquiries/InquiryActionButton';
import { InquirySpamReasons } from '../components/inquiries/InquirySpamReasons';
import { fetchThreadCounts } from '../api/notifications';
import { PhoneLink } from '../components/common/PhoneLink';
import { useInTabbedPage } from '../components/common/TabbedPage';

const STATUS_COLORS: Record<InquiryStatus, string> = {
  NEW: 'bg-blue-100 text-blue-800',
  CONTACTED: 'bg-yellow-100 text-yellow-800',
  ARCHIVED: 'bg-gray-100 text-gray-800',
  SPAM: 'bg-red-100 text-red-800',
};

/**
 * ⚠️ **"All" excludes spam, which is why Spam is its own tab.** The backend
 * omits quarantined rows unless a status is named — a tab that still showed
 * them would defeat the filter that put them there.
 */
const STATUS_FILTERS: { label: string; value: string }[] = [
  { label: 'All', value: '' },
  { label: 'New', value: 'NEW' },
  { label: 'Contacted', value: 'CONTACTED' },
  { label: 'Archived', value: 'ARCHIVED' },
  { label: 'Spam', value: 'SPAM' },
];

/**
 * ⚠️ **The conversation goes on the card, not behind a detail page.** There is
 * no per-inquiry route — enquiries are worked from this list — so a thread
 * reachable only from the notification bell would be a feature producers had to
 * be told about. It sits with the actions they already use.
 */
export function InquiriesPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [convertedMessage, setConvertedMessage] = useState<string | null>(null);
  const embedded = useInTabbedPage();
  const queryClient = useQueryClient();

  const { data: inquiries = [], isLoading } = useQuery<Inquiry[]>({
    queryKey: ['inquiries', statusFilter],
    queryFn: () => getInquiries(statusFilter ? { status: statusFilter } : undefined),
  });

  /*
   * One request for every card's badge, rather than one per card.
   *
   * `<RecordConversations>` without a `count` renders the package's
   * self-fetching button, which is right on a detail page and wrong here: this
   * list is unpaginated, so it would put a request on the wire per inquiry.
   * `fetchThreadCounts` chunks to the endpoint's 200-record limit rather than
   * truncating, so a long list costs a few requests instead of reporting every
   * inquiry past the 200th as having no conversation.
   *
   * Until it answers the cards are given `null` — "the page owns this badge and
   * does not know the number yet" — because rendering an unfetched count as
   * `0` claims a conversation does not exist.
   */
  const inquiryIds = inquiries.map((inquiry) => inquiry.id);
  const { data: threadCounts } = useQuery({
    queryKey: ['inquiry-thread-counts', inquiryIds],
    queryFn: () => fetchThreadCounts('Inquiry', inquiryIds),
    enabled: inquiryIds.length > 0,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: InquiryStatus }) =>
      updateInquiryStatus(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    },
  });

  const convertMutation = useMutation({
    mutationFn: (id: string) => convertInquiryToEvent(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      setConvertedMessage(
        `Event "${result.event.name}" created and user ${result.user.email} assigned.`,
      );
      setTimeout(() => setConvertedMessage(null), 5000);
    },
  });

  function formatDate(date: Date | string): string {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        {embedded ? (
          <span />
        ) : (
          <div className="flex items-center gap-3">
            <Mail className="h-6 w-6 text-gray-700" />
            <h1 className="text-2xl font-bold text-gray-900">Inquiries</h1>
          </div>
        )}
        <button
          onClick={() => queryClient.invalidateQueries({ queryKey: ['inquiries'] })}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {convertedMessage && (
        <div
          className="mb-4 px-4 py-3 bg-green-50 border border-green-200 text-green-800 text-sm rounded-lg"
          data-testid="convert-success"
        >
          {convertedMessage}
        </div>
      )}

      <div className="flex gap-2 mb-6">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.value}
            onClick={() => setStatusFilter(filter.value)}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              statusFilter === filter.value
                ? 'bg-gray-900 text-gray-50'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading inquiries...</div>
      ) : inquiries.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No inquiries found.</div>
      ) : (
        <div className="space-y-4">
          {inquiries.map((inquiry) => (
            <div
              key={inquiry.id}
              className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900">{inquiry.name}</h3>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[inquiry.status]}`}
                    >
                      {inquiry.status}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-gray-600">
                    <a href={`mailto:${inquiry.email}`} className="hover:underline">
                      {inquiry.email}
                    </a>
                    {inquiry.phone && (
                      <PhoneLink
                        phone={inquiry.phone}
                        name={inquiry.name}
                        wrapperClassName="ml-3"
                        className="hover:underline"
                      />
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-500">
                    {inquiry.eventType && (
                      <span>
                        <strong>Type:</strong> {inquiry.eventType}
                      </span>
                    )}
                    {inquiry.estimatedDate && (
                      <span>
                        <strong>Date:</strong> {inquiry.estimatedDate}
                      </span>
                    )}
                    {inquiry.estimatedGuestCount && (
                      <span>
                        <strong>Guests:</strong> {inquiry.estimatedGuestCount}
                      </span>
                    )}
                  </div>
                  {inquiry.message && (
                    <p className="mt-3 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                      {inquiry.message}
                    </p>
                  )}
                  {inquiry.status === 'SPAM' && (
                    <InquirySpamReasons reasons={inquiry.spamReasons} score={inquiry.spamScore} />
                  )}
                  <div className="mt-3">
                    <RecordConversations
                      entityType="Inquiry"
                      entityId={inquiry.id}
                      label="Conversation"
                      count={
                        threadCounts ? (threadCounts[inquiry.id] ?? { total: 0, unread: 0 }) : null
                      }
                    />
                  </div>
                  <div className="mt-3 text-xs text-gray-400">
                    Received {formatDate(inquiry.createdAt)}
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  {inquiry.status === 'SPAM' && (
                    <InquiryActionButton
                      icon={ShieldOff}
                      label="Not spam"
                      title="Release from quarantine — this was a real inquiry"
                      tone="success"
                      onClick={() => updateMutation.mutate({ id: inquiry.id, status: 'NEW' })}
                      disabled={updateMutation.isPending}
                    />
                  )}
                  {inquiry.status === 'NEW' && (
                    <InquiryActionButton
                      icon={ArrowRightCircle}
                      label="Convert"
                      title="Convert to event"
                      tone="primary"
                      onClick={() => convertMutation.mutate(inquiry.id)}
                      disabled={convertMutation.isPending}
                    />
                  )}
                  {inquiry.status !== 'CONTACTED' && inquiry.status !== 'SPAM' && (
                    <InquiryActionButton
                      icon={Check}
                      label="Contacted"
                      title="Mark as contacted"
                      tone="warning"
                      onClick={() => updateMutation.mutate({ id: inquiry.id, status: 'CONTACTED' })}
                      disabled={updateMutation.isPending}
                    />
                  )}
                  {inquiry.status !== 'SPAM' && (
                    <InquiryActionButton
                      icon={ShieldAlert}
                      label="Spam"
                      title="Mark as spam"
                      tone="danger"
                      onClick={() => updateMutation.mutate({ id: inquiry.id, status: 'SPAM' })}
                      disabled={updateMutation.isPending}
                    />
                  )}
                  {inquiry.status !== 'ARCHIVED' && inquiry.status !== 'SPAM' && (
                    <InquiryActionButton
                      icon={Archive}
                      label="Archive"
                      title="Archive"
                      tone="neutral"
                      onClick={() => updateMutation.mutate({ id: inquiry.id, status: 'ARCHIVED' })}
                      disabled={updateMutation.isPending}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
